import { ReviewedCartApiResponse, UserAddress, Location } from "@swiggy-private/cart-types";
import { GlobalHeaderState, SwgyHeaderKeysList, getBridgeSdk } from "@swiggy-private/checkout-core";
import { isEmpty, isVersionCodeEligible } from "@swiggy-private/core-ui";

// interfaces
import { AddressConfig } from "@cart-client/interfaces/Location";
import { ReviewedCart } from "@cart-client/interfaces/Cart";

export const getAllDeliveryValidAddresses = (
    addresses: ReviewedCartApiResponse["addresses"] = [],
): ReviewedCartApiResponse["addresses"] => {
    return addresses.filter(address => address.delivery_valid === 1);
};

export const hasSelectedAddress = (addresses: ReviewedCartApiResponse["addresses"] = []): boolean => {
    let hasSelected = false;
    addresses.filter(address => {
        if (address.selected) {
            hasSelected = true;
        }
    });

    return hasSelected;
};

export const getSelectedAddress = (addresses: ReviewedCartApiResponse["addresses"] = []): UserAddress | null => {
    let selectedAddress: UserAddress | null = null;
    addresses.filter(address => {
        if (address.selected) {
            selectedAddress = address;
        }
    });

    return selectedAddress;
};

export const getCanShowCancellationPopup = (disableCancellationPopupXP: boolean, isNewUser: boolean): boolean => {
    // Check to not show cancellation policy in iOS
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    if (platform.toLowerCase() === "swiggy-ios") {
        return false;
    }

    return isNewUser && !disableCancellationPopupXP;
};

/**
 * Using harvesine formula to compute distance between 2 points
 * @param locationA location A
 * @param locationB location B
 * @returns distance in metres
 */
const calculateDistanceWithLatLngs = (locationA: Location, locationB: Location): number => {
    const p = 0.017453292519943295; // Math.PI / 180
    const c = Math.cos; // keeps constanst value of Math.cos
    const a =
        0.5 -
        c((Number(locationB.lat) - Number(locationA.lat)) * p) / 2 +
        (c(Number(locationA.lat) * p) *
            c(Number(locationB.lat) * p) *
            (1 - c((Number(locationB.lng) - Number(locationA.lng)) * p))) /
            2;

    return 12742 * Math.asin(Math.sqrt(a)) * 1000; // 2 * R; R = 6371 km
};

export const getAddressConfugration = (addresses: ReviewedCartApiResponse["addresses"] = []): AddressConfig => {
    const addressConfig: AddressConfig = {
        serviceableAddressWithinXMetres: [],
        serviceableAddressOutsideXMetres: [],
        hasAddressWithinXMetres: false,
        hasAddressOutsideXMetres: false,
    };
    const lat = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat);
    const lng = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng);

    if (!lat || !lng || isEmpty(addresses)) {
        return addressConfig;
    }

    const userLocation = {
        lat,
        lng,
    };

    addresses.map(address => {
        const distance = calculateDistanceWithLatLngs(userLocation, address);
        if (distance < 500) {
            if (address.delivery_valid === 1) {
                addressConfig.serviceableAddressWithinXMetres.push(address);
            }
            addressConfig.hasAddressWithinXMetres = true;
        } else {
            if (address.delivery_valid === 1) {
                addressConfig.serviceableAddressOutsideXMetres.push(address);
            }
            addressConfig.hasAddressOutsideXMetres = true;
        }
    });

    return addressConfig;
};

export const goToSimilarRestuarantAnalyticsContext = (
    cartResponse: ReviewedCart | null,
    cartAmount: number,
): string => {
    return JSON.stringify({
        collectionId: cartResponse?.cta_meta?.collection_id || "",
        restaurantId: cartResponse?.cta_meta?.restaurant_id || "",
        cityId: cartResponse?.cta_meta?.city_id || "",
        cartAmount,
    });
};

export const goToSimilarRestuarantHomeAnalyticsContext = (
    cartResponse: ReviewedCart | null,
    cartAmount: number,
): string => {
    return JSON.stringify({
        restaurantId: cartResponse?.data?.restaurant_details?.id || "",
        cartAmount,
    });
};

export const canSendFlipkartInstalledEvent = async (): Promise<boolean> => {
    try {
        const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode) || "";
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        const MIN_ANDROID_VERSION = 1201;
        const MIN_IOS_VERSION = 503;
        if (
            isVersionCodeEligible(MIN_ANDROID_VERSION, MIN_IOS_VERSION, versionCode, platform) &&
            (await isFlipkartAppInstalled())
        ) {
            return true;
        }
        return false;
    } catch (error) {
        return false;
    }
};

export const isFlipkartAppInstalled = async (): Promise<boolean> => {
    try {
        // check if flipkart app is installed
        const response = await getBridgeSdk().getInstalledApps({ apps: ["flipkart"] });
        return response?.apps?.includes("flipkart") ? true : false;
    } catch (error) {
        return false;
    }
};
